/**
 * @fileoverview A etapa 1 da contratação (03/11/2026): o plano, as licenças e os
 * complementos. Os cartões mostram só o que muda entre os planos, com as linhas
 * alinhadas; o que os dois têm fica numa frase embaixo. Os complementos começam
 * vazios: nada pago vem marcado. As regras de cada escolha ficam nos ⓘ.
 */

import React from "react";
import { MenuItem } from "@material-ui/core";
import { Bot, Building2, Check, MessagesSquare, Smartphone, SquareKanban, Users } from "lucide-react";

import ModernSelect from "../../ModernSelect";
import { NumberStepper, RadioGroup } from "../PlanControls";
import { InfoTip } from "../../../pages/Flows/components/TriageManager/InfoTip";
import { i18n } from "../../../translate/i18n";
import { money, thousands } from "../../../utils/billingPlans";

const t = (key, options) => i18n.t(`plansDialog.${key}`, options);

const PERKS = [
  { key: "accounts", icon: Building2, field: "accounts" },
  { key: "whatsapps", icon: Smartphone, field: "whatsapps" },
  { key: "boards", icon: SquareKanban, field: "crmBoards" },
];

const PlanCard = ({ classes, item, selected, current, disabled, buttonProps }) => (
  <button type="button" className={`${classes.planCard} ${selected ? classes.planCardOn : ""} ${disabled ? classes.planCardOff : ""}`} {...buttonProps}>
    <span className={classes.planHead}>
      <span className={classes.planTitle}>
        {item.name}
        {current && <span className={classes.planTag}>{t("planCurrent")}</span>}
      </span>
      <span className={`${classes.planRadio} ${selected ? classes.planRadioOn : ""}`} aria-hidden="true">
        {selected && <Check strokeWidth={3} />}
      </span>
    </span>
    <span className={classes.planPriceRow}>
      <span className={classes.planAmount}>{money(item.pricePerUserCents)}</span>
      <span className={classes.planUnit}>{t("perSeatMonth")}</span>
    </span>
    <span className={classes.planPerks}>
      {PERKS.map(({ key, icon: Icon, field }) => {
        const unlimited = item[field] === null;
        return (
          <span key={key} className={`${classes.planPerk} ${unlimited ? classes.planPerkStrong : ""}`}>
            <Icon aria-hidden="true" />
            {unlimited ? t(`perks.${key}Unlimited`) : t(`perks.${key}`, { count: item[field] })}
          </span>
        );
      })}
    </span>
  </button>
);

/** Uma escolha: o ícone, o nome com a regra no ⓘ, uma frase e, à direita, o controle. */
export const OptionRow = ({ classes, icon: Icon, name, nameId, help, hint, wide = false, children }) => (
  <div className={classes.row}>
    <span className={classes.rowIcon} aria-hidden="true">
      <Icon />
    </span>
    <div className={classes.rowText}>
      <span className={classes.rowName}>
        <span id={nameId}>{name}</span>
        {help && <InfoTip text={help} />}
      </span>
      {hint && <span className={classes.rowHint}>{hint}</span>}
    </div>
    <div className={`${classes.rowControl} ${wide ? classes.rowControlWide : ""}`}>{children}</div>
  </div>
);

/** A linha do Chat Interno (a etapa 1 e a contratação só dele usam a mesma). */
export const InternalChatRow = ({ classes, catalog, help, value, min, max, onChange }) => (
  <OptionRow
    classes={classes}
    icon={MessagesSquare}
    name={t("internal")}
    help={help}
    hint={
      <>
        {t("internalHint")}
        <br />
        {t("internalPrice", { price: money(catalog.internalChatPriceCents) })}
      </>
    }
  >
    <NumberStepper classes={classes} name={t("internal")} value={value} min={min} max={max} onChange={onChange} />
  </OptionRow>
);

const CustomizeStep = ({ classes, titleRef, catalog, changeMode, current, plan, onPlanChange, definition, seats, minSeats, onSeatsChange, internalChatSeats, minInternal, maxInternal, onInternalChange, aiPackMessages, onAiPackChange }) => {
  const paidPlans = catalog.plans.filter((item) => item.key !== "starter");
  return (
    <div className={classes.stepContent}>
      <section className={classes.section} aria-labelledby="plans-dialog-plan">
        <span className={classes.sectionTitleRow}>
          <h3 className={classes.sectionTitle} id="plans-dialog-plan" ref={titleRef} tabIndex={-1}>
            {t("choosePlan")}
          </h3>
          {changeMode && <InfoTip text={t("planChangeInfo")} />}
        </span>
        <RadioGroup
          className={classes.plans}
          labelledBy="plans-dialog-plan"
          options={paidPlans.map((item) => ({ value: item.key, item, disabled: changeMode && current.plan === "pro" && item.key !== "pro" }))}
          value={plan}
          onChange={onPlanChange}
          renderOption={({ value, item, disabled }, props) => (
            <PlanCard classes={classes} item={item} selected={value === plan} current={changeMode && value === current.plan} disabled={disabled} buttonProps={props} />
          )}
        />
        <p className={classes.bothPlans} style={{ margin: 0 }}>
          <Check aria-hidden="true" />
          <span>{t("bothPlans", { ai: thousands(definition.aiMessages) })}</span>
        </p>
      </section>

      <div className={classes.rows}>
        <OptionRow
          classes={classes}
          icon={Users}
          name={t("seats")}
          help={changeMode ? t("seatsHelpChange", { min: minSeats }) : t("seatsHelp", { min: minSeats, billed: definition.minBilledUsers })}
          hint={t("seatsHint", { min: minSeats })}
        >
          <NumberStepper classes={classes} name={t("seats")} value={seats} min={minSeats} max={1000} onChange={onSeatsChange} />
        </OptionRow>
      </div>

      <section className={classes.section} aria-labelledby="plans-dialog-addons">
        <h3 className={classes.sectionTitle} id="plans-dialog-addons">
          {t("addons")}
          <span className={classes.optionalTag}>{t("optional")}</span>
        </h3>
        <div className={classes.rows}>
          <InternalChatRow classes={classes} catalog={catalog} help={t("internalHelp")} value={internalChatSeats} min={minInternal} max={maxInternal} onChange={onInternalChange} />
          {definition.aiPacks && (
            <OptionRow classes={classes} icon={Bot} name={t("aiPack")} nameId="plans-dialog-pack" help={t("aiPackHelp")} hint={t("aiPackHint", { ai: thousands(definition.aiMessages) })} wide>
              <ModernSelect
                size="small"
                value={aiPackMessages}
                onChange={(event) => onAiPackChange(event.target.value)}
                labelId="plans-dialog-pack"
                displayEmpty
                MenuProps={{ disableScrollLock: true }}
                className={classes.packSelect}
              >
                <MenuItem value="" disabled={changeMode && !!current.aiPackMessages}>
                  {t("aiPackNoneShort")}
                  {changeMode && !current.aiPackMessages ? ` ${t("packCurrent")}` : ""}
                </MenuItem>
                {catalog.aiPacks.map((pack) => (
                  <MenuItem key={pack.messages} value={pack.messages} disabled={changeMode && (current.aiPackMessages ?? 0) > pack.messages}>
                    {t("aiPackOptionShort", { messages: thousands(pack.messages), price: money(pack.priceCents) })}
                    {changeMode && current.aiPackMessages === pack.messages ? ` ${t("packCurrent")}` : ""}
                  </MenuItem>
                ))}
              </ModernSelect>
            </OptionRow>
          )}
        </div>
      </section>
    </div>
  );
};

export default CustomizeStep;
